import { useState } from 'react';
import type { JSX } from 'react';
import {
  ConfirmDialog,
  SettingsLock,
  SettingsMenu,
  SettingsMessage,
  SettingsPanel,
  SettingsRow,
  SettingsSection,
  SettingsTable,
  SkeletonText,
  StatusBadge,
  Switch,
  useInstallationPort,
} from '@taki/ui';
import type { EnvironnementDeploiement } from '@taki/ui';
import { useDeprecatedAppIds } from '../buildStamp.js';
import { idsInconnus } from '../installation.js';
import type { ShellAppEntry } from '../types.js';
import { messageDErreur } from './MembresSettingsPanel.js';

/**
 * PARAMETRES > APPS INSTALLEES : ce que l'org a installe, et ou elle tourne.
 *
 * LA LISTE PROPOSEE EST CELLE QUE CE BUNDLE SAIT SERVIR (le registre de l'hote,
 * `catalogue`), chacune avec son interrupteur. La liste de l'org vit dans sa
 * base (`orgs/{orgId}/settings/apps`) : installer l'ajoute en fin de dock,
 * desinstaller la retire, rien d'autre. DESINSTALLER NE SUPPRIME AUCUNE DONNEE :
 * la fenetre de confirmation le dit, et reinstaller retrouve tout.
 *
 * L'ENVIRONNEMENT (la bande STAGING) se regle ici aussi, par l'owner seul.
 *
 * Qui n'a pas le droit voit les controles GRISES, avec leur raison (G16), jamais
 * caches : savoir ce qui est installe sert a tout le monde. Les rules refusent
 * de toute facon (`firebase/firestore.rules`, blocs `settings/apps` et
 * `settings/environnement`).
 */

const ENVIRONNEMENTS: ReadonlyArray<{ value: EnvironnementDeploiement; label: string }> = [
  { value: 'production', label: 'Production' },
  { value: 'staging', label: 'Test (staging)' },
];

export interface InstallationSettingsPanelProps {
  /** Toutes les apps que ce bundle sait servir, installees ou non. */
  catalogue: readonly ShellAppEntry[];
}

export function InstallationSettingsPanel({ catalogue }: InstallationSettingsPanelProps): JSX.Element {
  const port = useInstallationPort();
  const deprecated = useDeprecatedAppIds();
  const [enCours, setEnCours] = useState<string | null>(null);
  const [aDesinstaller, setADesinstaller] = useState<{ id: string; label: string } | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  if (port === null || !port.pret) {
    return (
      <SettingsPanel>
        <SettingsSection title="Applications">
          <SkeletonText lines={4} />
        </SettingsSection>
      </SettingsPanel>
    );
  }

  const installees = new Set(port.apps);
  const inconnues = idsInconnus(port.apps, catalogue);
  const estDepreciee = (id: string) => deprecated.kind === 'known' && deprecated.ids.has(id);
  const raisonEnvironnement = port.raisonEnvironnement?.startsWith('Org master :')
    ? 'Géré par Taki.'
    : port.raisonEnvironnement;

  const jouer = (id: string, geste: () => Promise<void>) => {
    setErreur(null);
    setEnCours(id);
    geste().then(
      () => setEnCours(null),
      (cause: unknown) => {
        setEnCours(null);
        setErreur(messageDErreur(cause));
      },
    );
  };

  const basculer = (entree: { id: string; label: string }, installer: boolean) => {
    if (installer) jouer(entree.id, () => port.installer(entree.id));
    else setADesinstaller(entree);
  };

  const raisonApps = port.raisonApps?.startsWith('Org master :') ? 'Géré par Taki.' : port.raisonApps;

  return (
    <SettingsPanel>
      <SettingsSection title="Applications">
        <SettingsTable
          label="Applications"
          columns={[{ label: 'App' }, { label: 'Installée', fin: true, largeur: 'w-24' }]}
          rows={[
            ...catalogue.map((entree) => {
              const installee = installees.has(entree.id);
              const depreciee = estDepreciee(entree.id);
              const verrou = !port.peutGererLesApps || enCours !== null || (depreciee && !installee);
              return {
                id: entree.id,
                cells: [
                  <span key="app" className="flex items-center gap-2.5">
                    <span aria-hidden className="flex h-4 w-4 shrink-0 items-center justify-center text-ink-500 [&_svg]:h-4 [&_svg]:w-4">{entree.icon}</span>
                    <span className="font-medium text-ink-900">{entree.label}</span>
                    {depreciee && <StatusBadge tone="neutral" label="Dépréciée" />}
                  </span>,
                  <Switch
                    key="installee"
                    checked={installee}
                    locked={verrou}
                    {...(verrou
                      ? { lockedReason: raisonApps ?? (depreciee ? 'Installation retirée.' : 'Modification en cours.') }
                      : {})}
                    aria-label={`${installee ? 'Désinstaller' : 'Installer'} ${entree.label}`}
                    onChange={(suivant) => basculer({ id: entree.id, label: entree.label }, suivant)}
                  />,
                ],
              };
            }),
            /* Installee en base, absente de ce bundle : on peut encore la desinstaller. */
            ...inconnues.map((id) => ({
              id,
              cells: [
                <span key="app" className="flex flex-wrap items-center gap-2.5">
                  <span className="font-medium text-ink-900">{id}</span>
                  <StatusBadge tone="neutral" label="Indisponible" />
                  <SettingsLock label="Installée, mais absente de cette version." />
                </span>,
                <Switch
                  key="installee"
                  checked
                  locked={!port.peutGererLesApps || enCours !== null}
                  {...(!port.peutGererLesApps ? { lockedReason: raisonApps ?? '' } : {})}
                  aria-label={`Désinstaller ${id}`}
                  onChange={() => setADesinstaller({ id, label: id })}
                />,
              ],
            })),
          ]}
        />
      </SettingsSection>

      <SettingsSection title="Environnement">
        <SettingsRow
          label="Mode"
          lock={port.peutChangerLEnvironnement ? undefined : raisonEnvironnement ?? 'Réservé au propriétaire.'}
        >
          <SettingsMenu
            label="Mode de l’environnement"
            value={port.environnement}
            options={ENVIRONNEMENTS}
            disabled={!port.peutChangerLEnvironnement || enCours !== null}
            onChange={(valeur) => {
              jouer('environnement', () => port.changerEnvironnement(valeur === 'staging' ? 'staging' : 'production'));
            }}
          />
        </SettingsRow>
      </SettingsSection>

      {erreur != null && <SettingsMessage tone="error">{erreur}</SettingsMessage>}

      <ConfirmDialog
        open={aDesinstaller !== null}
        tone="neutral"
        title={`Désinstaller ${aDesinstaller?.label ?? ''} ?`}
        body={['Données conservées. Réinstaller les retrouve.']}
        confirmLabel="Désinstaller"
        onCancel={() => setADesinstaller(null)}
        onConfirm={() => {
          const cible = aDesinstaller;
          setADesinstaller(null);
          if (cible !== null) jouer(cible.id, () => port.desinstaller(cible.id));
        }}
      />
    </SettingsPanel>
  );
}
